import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import SvgIcon from '@mui/material/SvgIcon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { Alert, Information } from '@opengov/react-capital-assets';
import { TABLE } from '@opengov/stories-shared/constants';
import { Map } from 'immutable';
import React, { useEffect, useRef, useState } from 'react';
import {
  IPublicationReport,
  IPublicationViewMessages,
  IStoryEmbedsResponse
} from '../../../../../types/publications/publicationListTypes';
import { ADD_TABLE_TILE, ADD_TILE, EMBED_SYNC_SETTING } from '../../../../constants';
import { getReportType } from '../../../../utils/helper';
import { styles } from './PublicationReportsTable.styles';

const selectableTab = ['rect', 'path', 'svg', 'LABEL', 'INPUT', 'polygon'];

type PublicationReportsTableProps = {
  messages: IPublicationViewMessages;
  data: IPublicationReport[];
  selectedItems: Map<string, IPublicationReport>;
  failedEmbedUpdateIds: string[];
  setSelectedItems: React.Dispatch<React.SetStateAction<Map<string, IPublicationReport>>>;
  handleRowOnSelect: (currentItem: IPublicationReport) => void;
  handleRowOnSelectAll(): void;
  onOpenTileModal(modal: string, record: IStoryEmbedsResponse): void;
  paginationOptions: {
    totalRows: number;
    rowsPerPage: number;
    setRowsPerPage: (rowsPerPage: number) => void;
    currentPage: number;
    setPage: (page: number) => void;
    itemName: string;
  };
};

const getFrequencyUpdateCaption = (type: string) => {
  let caption = '';
  switch (type) {
    case EMBED_SYNC_SETTING.LIVE:
      caption = 'Live view';
      break;
    case EMBED_SYNC_SETTING.REFRESH_ON_UPDATE:
      caption = 'Refresh on update';
      break;
    case EMBED_SYNC_SETTING.SNAPSHOT:
      caption = 'Snapshot';
      break;
    default:
      break;
  }
  return caption;
};

export function PublicationReportsTable(props: PublicationReportsTableProps) {
  const {
    data,
    onOpenTileModal,
    selectedItems,
    handleRowOnSelect,
    handleRowOnSelectAll,
    messages,
    failedEmbedUpdateIds,
    paginationOptions
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [tableMaxHeight, setTableMaxHeight] = useState<string>('calc(100vh - 500px)');

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const offsetTop = containerRef.current.getBoundingClientRect().top;
        setTableMaxHeight(`calc(100vh - ${offsetTop + 52 + 8}px)`);
      }
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (containerRef.current) observer.observe(document.body);
    window.addEventListener('resize', updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  const isAllItemsSelected = (): boolean => {
    const selectedItemArray = Array.from(selectedItems.keys());
    const items = data.filter((record) => !record.isViewDeleted && !failedEmbedUpdateIds.includes(record.embedId));
    return !!items.length && items.every((item) => selectedItemArray.includes(`${item.embedId}-${item.story._id}`));
  };

  const showError = (record: IPublicationReport) =>
    record.isViewDeleted || failedEmbedUpdateIds.includes(record.embedId);

  const getTooltipMessage = (record: IPublicationReport) => {
    const message = record.isViewDeleted ? messages.reportDeletedMessage : messages.reportLoadFailedMessage;
    return message;
  };

  const getErrorIcon = (record: IPublicationReport) =>
    failedEmbedUpdateIds.includes(record.embedId) ? (
      <SvgIcon component={Information} color="error" sx={styles.icon} inheritViewBox />
    ) : (
      <SvgIcon component={Alert} color="warning" sx={styles.icon} inheritViewBox />
    );

  return (
    <div data-test="report-embedlist-container">
      {data.length > 0 ? (
        <Box ref={containerRef} sx={styles.tableContainer}>
          <TableContainer sx={{ ...styles.table, maxHeight: tableMaxHeight }}>
            <Table stickyHeader>
              <TableHead data-test="report-embedlist-table-headers">
                <TableRow>
                  <TableCell sx={styles.checkboxHeaderCell} aria-label="Select">
                    <Checkbox
                      checked={isAllItemsSelected()}
                      onChange={handleRowOnSelectAll}
                      id="report-embedlist-table-checkbox-all"
                      inputProps={{ 'aria-label': 'Select all reports' }}
                    />
                  </TableCell>
                  <TableCell sx={styles.iconHeader} aria-label="Status indicator"></TableCell>
                  <TableCell sx={styles.headerCell}>Report Name</TableCell>
                  <TableCell sx={styles.headerCell}>View</TableCell>
                  <TableCell sx={styles.headerCell}>Story</TableCell>
                  <TableCell sx={styles.headerCell}>Type</TableCell>
                  <TableCell sx={styles.headerCell}>Data Update Frequency</TableCell>
                </TableRow>
              </TableHead>
              <TableBody data-test="report-embedlist-table-body">
                {data.map((record) => {
                  const isDisabled = record.isViewDeleted || failedEmbedUpdateIds.includes(record.embedId);
                  const isSelected = Boolean(
                    selectedItems.size && selectedItems.has(`${record.embedId}-${record.story._id}`)
                  );

                  const openReportModal = () => {
                    // TODO: replace compose-app calls (publicationViewActionCreators, storyViewEffects, openModal)
                  };

                  return (
                    <TableRow
                      data-test="report-embedlist-row"
                      key={`${record.embedId}-${record.story._id}`}
                      selected={isSelected}
                      tabIndex={isDisabled ? undefined : 0}
                      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget && !isDisabled) { e.preventDefault(); openReportModal(); } }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (selectableTab.includes((e.target as HTMLElement).tagName)) {
                          if (!isDisabled) {
                            handleRowOnSelect(record);
                          }
                        } else {
                          const modal = getReportType(record.type) === TABLE ? ADD_TABLE_TILE : ADD_TILE;
                          onOpenTileModal(modal, record as unknown as IStoryEmbedsResponse);
                        }
                      }}
                      sx={isDisabled ? styles.tableRowDisabled : styles.tableRow}
                    >
                      <TableCell
                        sx={styles.checkboxBodyCell}
                        onClick={(e) => e.stopPropagation()}
                        data-test="report-embedlist-table-checkbox-cell"
                      >
                        <Checkbox
                          id={`report-embedlist-table-checkbox-${record.story._id}-${record.embedId}`}
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => {
                            if (!isDisabled) {
                              handleRowOnSelect(record);
                            }
                          }}
                          inputProps={{ 'aria-label': `Select report ${record.story._id}` }}
                        />
                      </TableCell>
                      <TableCell style={styles.iconCell} data-test="report-embedlist-table-icon-cell">
                        {showError(record) ? (
                          <Tooltip title={getTooltipMessage(record)} placement="top">
                            <Box sx={styles.iconContainer} tabIndex={0}>{getErrorIcon(record)}</Box>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                      <TableCell sx={styles.bodyCell} data-test="report-embedlist-table-title-cell">
                        <Tooltip title={record.title}>
                          <Box sx={[styles.bold, record.isViewDeleted && styles.italics]}>{record.title}</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={styles.bodyCell} data-test="report-embedlist-table-saved-view-title-cell">
                        <Tooltip title={record.savedViewTitle}>
                          <Box sx={record.isViewDeleted ? styles.italics : undefined}>{record.savedViewTitle}</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={styles.bodyCell} data-test="report-embedlist-table-story-title-cell">
                        <Tooltip title={record.story.title}>
                          <Box>{record.story.title}</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={styles.bodyCell} data-test="report-embedlist-table-report-type-cell">
                        {getReportType(record.type)}
                      </TableCell>
                      <TableCell sx={styles.bodyCell} data-test="report-embedlist-table-frequency-cell">
                        {getFrequencyUpdateCaption(record.frequency)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={paginationOptions.totalRows}
            page={paginationOptions.currentPage - 1}
            rowsPerPage={paginationOptions.rowsPerPage}
            rowsPerPageOptions={[20, 40, 60]}
            onPageChange={(_, newPage) => paginationOptions.setPage(newPage + 1)}
            onRowsPerPageChange={(e) => paginationOptions.setRowsPerPage(parseInt(e.target.value, 10))}
            data-test="publication-reports-pagination"
            sx={styles.pagination}
          />
        </Box>
      ) : null}
    </div>
  );
}
